import { Box, ClickAwayListener, Grid, Grid2, Typography } from '@mui/material'

import { StepType, TourProps, TourProvider, useTour } from '@reactour/tour'
import Button from 'components/buttons/Button'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { VideoOnboardingContent } from 'components/onboarding/VideoOnboardingContent'
import { t } from 'i18next'
import {
  createContext,
  ReactElement,
  useContext,
  useState,
  useEffect
} from 'react'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp } from 'utils/appType'
import { getUserABGroup } from 'utils/onboarding'
import { isUserOutOfTierBasedOnLandline } from 'utils/tier'

import { withObservables } from '@nozbe/watermelondb/react'
import { ComponentType } from 'react'
import compose from 'recompose/compose'
import ObserveService from 'services/db/Observe'
import { AccountDoc } from 'types/api/changes'
import { useLocalStorageState } from 'hooks/usePersistState'
import { isTrulyEmpty } from 'utils/object'
import AreYouSureDialog from 'components/dialogs/AreYouSureDialog'
import i18n from 'i18n'
import { isTestEnv } from 'utils/env'
import { useCalendar } from 'components/providers/CalendarProvider'

export interface TFMTourProviderProps {
  children: React.ReactNode
  onClickMask?: () => void
  mode: ONBOARDING_MODE
  account?: Model<AccountDoc>
}

export interface SingleTutor {
  id: string
  title: string
  message: string
}

interface TFMTourProviderContextProps {
  mode: ONBOARDING_MODE | undefined
  setMode: (mode: ONBOARDING_MODE) => void
  setSingleTutorial: (tutorial: SingleTutor) => void
  showStepIfNeeded: (step: string, tourProps: TourProps) => boolean
  getStepsByMode?: (
    mode: ONBOARDING_MODE,
    precondition?: () => boolean
  ) => StepType[]
  wasPlayed: boolean | undefined
  setWasPlayed: (value: boolean | undefined) => void
  wasUnMuted: boolean | undefined
  setWasUnMuted: (value: boolean | undefined) => void
  hasEnded: boolean
  setHasEnded: (value: boolean) => void
  reset: () => void
  isPlaying: boolean
  setIsPlaying: (value: boolean) => void
  isMuted: boolean
  setIsMuted: (value: boolean) => void
  showSureEndDialog?: boolean
  setShowSureEndDialog?: (value: boolean) => void
  isStepCompleted?: (stepId: string) => boolean
  setStepCompleted?: (stepId: string) => void
}

const TFMTourProviderContext = createContext<TFMTourProviderContextProps>({
  mode: undefined,
  setMode: (mode: ONBOARDING_MODE) => {},
  setSingleTutorial: (tutorial: SingleTutor) => {},
  showStepIfNeeded: (step: string, tourProps: TourProps) => false,
  getStepsByMode: (mode: ONBOARDING_MODE) => [],
  wasPlayed: undefined,
  setWasPlayed: () => {},
  wasUnMuted: undefined,
  setWasUnMuted: () => {},
  hasEnded: false,
  setHasEnded: () => {},
  reset: () => {},
  isPlaying: false,
  setIsPlaying: () => {},
  isMuted: true,
  setIsMuted: () => {},
  showSureEndDialog: false,
  setShowSureEndDialog: () => {},
  isStepCompleted: () => false,
  setStepCompleted: () => {}
})

export enum ONBOARDING_MODE {
  FIRST_LOGIN = 'FIRST_LOGIN',
  DIMMED = 'DIMMED',
  FROM_GROUP = 'FROM_GROUP',
  FROM_ALL = 'FROM_ALL',
  DUPLICATED = 'DUPLICATED'
}

export interface VideoContainerProps {
  children: React.ReactNode
  position?: 'bottom-right' | 'center' | 'custom'
  top?: string
  bottom?: string
  left?: string
  right?: string
}

export function TFMTourProvider(props: TFMTourProviderProps) {
  const account = props.account
  const [mode, setMode] = useState<ONBOARDING_MODE>(props.mode)

  const [singleTutorDialog, setSingleTutorialDialog] = useState<
    SingleTutor | undefined
  >(undefined)

  const [isMuted, setIsMuted] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [wasPlayed, setWasPlayed] = useState<boolean | undefined>(undefined)
  const [wasUnMuted, setWasUnMuted] = useState<boolean | undefined>(undefined)
  const [hasEnded, setHasEnded] = useState(false)
  const [showSureEndDialog, setShowSureEndDialog] = useState(false)

  const setSingleTutorial = (tutorial: SingleTutor) => {
    if (checkCompletedStep(tutorial.id)) {
      return
    }

    LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
    setSingleTutorialDialog(tutorial)
  }

  function showStepIfNeeded(step: string, tourProps: TourProps): boolean {
    const state = checkCompletedStep(step)

    if (!state) {
      tourProps.setCurrentStep(tourProps.currentStep + 1)
      tourProps.setIsOpen(true)
    }

    return state
  }

  useEffect(() => {
    reset()
  }, [mode])

  function setCurrentMode(newMode: ONBOARDING_MODE) {
    if (mode === newMode) {
      setWasPlayed(wasPlayed)
      setIsPlaying(getStepsByMode(newMode).length > 0)
      setHasEnded(false)
    } else {
      setMode(newMode)
    }
  }

  function reset() {
    setIsMuted(true)
    setIsPlaying(false)
    setWasPlayed(undefined)
    setWasUnMuted(undefined)
    setHasEnded(false)
  }

  function isStepCompleted(stepId: string): boolean {
    const completedSteps = JSON.parse(
      LocalStorage.get(LocalStorageKey.STATE_DICT) || '{}'
    )
    return completedSteps[stepId] === true
  }

  const contextValue: TFMTourProviderContextProps = {
    mode,
    setMode: setCurrentMode,
    setSingleTutorial,
    showStepIfNeeded: showStepIfNeeded,
    getStepsByMode: getStepsByMode,
    wasPlayed,
    setWasPlayed,
    wasUnMuted,
    setWasUnMuted,
    hasEnded,
    setHasEnded,
    reset,
    isPlaying,
    setIsPlaying,
    isMuted,
    setIsMuted,
    showSureEndDialog,
    setShowSureEndDialog,
    isStepCompleted: isStepCompleted,
    setStepCompleted: completeTutorialStep
  }

  const [landlineDialog, setLandlineDialog] = useLocalStorageState(
    LocalStorageKey.LANDLINE_DIALOG,
    LocalStorage.get(LocalStorageKey.LANDLINE_DIALOG) ?? '1'
  )

  Logger.blue('TFMTourProvider', { mode })
  Logger.blue(
    'TFMTourProvider:OutOfTier',
    isUserOutOfTierBasedOnLandline(account)
  )
  Logger.blue('TFMTourProvider:Account', account)
  Logger.blue('TFMTourProvider:LandlineDialog', landlineDialog)

  const steps =
    String(landlineDialog) === '1' ||
    LocalStorage.get(LocalStorageKey.USER_FINSHED_ONBOARDING) === '1'
      ? []
      : getStepsByMode(mode)

  Logger.blue('TFMTourProvider:Steps', steps)

  if (!account) return <>{props.children}</>

  return (
    <TFMTourProviderContext.Provider value={contextValue}>
      <TourProvider
        styles={{
          popover: (base) => ({
            ...base,
            '--reactour-accent': '#ef5a3d',
            borderRadius: 16,
            left: '8px',
            padding: '30px 30px 0px 30px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            maxHeight: 'none',
            overflow: 'visible'
          }),
          maskWrapper: (base) => ({
            ...base,
            color: 'rgba(0, 0, 0, 0.5)'
          }),
          maskArea: (base) => ({
            ...base,

            rx: 18 // Rounded corners
          })
        }}
        scrollSmooth
        steps={steps}
        showBadge={false}
        showCloseButton={false}
        showPrevNextButtons={false}
        showDots={false}
        disableKeyboardNavigation
        disableDotsNavigation
        onClickMask={({ setCurrentStep, currentStep, steps, setIsOpen }) => {
          if (props.onClickMask) {
            props.onClickMask()
          }
          Logger.orange('TFMTourProvider:ClickMask Next Step')
          setShowSureEndDialog(true)
        }}
      >
        {props.children}
        {singleTutorDialog && !isUserOutOfTierBasedOnLandline(account) && (
          <ConfirmationDialog
            title={singleTutorDialog.title}
            content={singleTutorDialog.message}
            open={true}
            handleClose={() => {
              completeTutorialStep(singleTutorDialog.id)
            }}
            leftButtonHidden
            handleRightButton={() => {
              setSingleTutorialDialog(undefined)
              completeTutorialStep(singleTutorDialog.id)
            }}
          />
        )}
        <Content />
      </TourProvider>
    </TFMTourProviderContext.Provider>
  )
}

function Content() {
  const tour = useTour()
  const { isOpen, currentStep, steps, setCurrentStep, setIsOpen } = tour

  const props: OnboardingContentProps = (
    steps[currentStep]?.content as ReactElement
  )?.props

  return props?.blockUserInteraction === true &&
    isOpen &&
    currentStep !== steps.length - 1 ? (
    <Box
      onClick={(e) => {
        // e.preventDefault()
        // e.stopPropagation()
        // e.nativeEvent.stopImmediatePropagation()
      }}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        backgroundColor: 'transparent' // Semi-transparent background
      }}
    />
  ) : (
    <></>
  )
}

export interface OnboardingContentProps {
  title: string
  subtitle: string
  doNotMoveStep?: boolean
  conditionalStep?: (props: TourProps) => void
  blockUserInteraction?: boolean
  nextButtonAction?: (props: TourProps) => void
}

export function OnboardingContent(props: OnboardingContentProps) {
  const tour = useTour()
  const { currentStep, steps, setCurrentStep, setIsOpen } = tour

  const performConditionalLogic = () => {
    setIsOpen(false)

    if (props.conditionalStep) {
      props.conditionalStep(tour)
    }
  }

  const moveForward = () => {
    if (currentStep === steps.length - 1) {
      setIsOpen(false)
      return
    }

    setCurrentStep((s) => s + 1)
  }

  const proceed = () => {
    if (props.nextButtonAction) {
      props.nextButtonAction(tour)
    }

    if (props.doNotMoveStep) {
      performConditionalLogic()
      return
    }

    moveForward()
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => {
        // do nothing
        // if (!props.blockUserInteraction) {
        //   proceed()
        // } else {
        //   moveForward()
        // }
      }}
    >
      <Grid container direction={'column'} spacing={2}>
        <Typography
          id="onboarding-title"
          fontWeight={600}
          fontSize={'1.1rem'}
          paddingBottom={'10px'}
          paddingTop={'5px'}
        >
          {props.title}
        </Typography>
        <Typography id="onboarding-subtitle" fontSize={'15px'}>
          {props.subtitle}
        </Typography>
        <Grid2
          paddingTop={'16px'}
          direction={'row'}
          container
          justifyContent={'space-between'}
        >
          <Typography
            id="onboarding-quit-button"
            sx={{
              textDecoration: 'underline',
              color: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer'
            }}
            onClick={() => {
              setIsOpen(false)
              LocalStorage.set(LocalStorageKey.USER_FINSHED_ONBOARDING, '1')
            }}
            fontSize={'15px'}
          >
            {t('quiteTutorial')}
          </Typography>
          <Button id="onboarding-next-button" onClick={proceed} size="xs">
            {props.nextButtonAction
              ? t('doIt')
              : props.doNotMoveStep || currentStep === steps.length - 1
                ? t('continue')
                : t('nextTip')}
          </Button>
        </Grid2>
      </Grid>
    </ClickAwayListener>
  )
}

function getStepsByMode(
  mode: ONBOARDING_MODE,
  precondition?: () => boolean
): StepType[] {
  if (precondition && !precondition()) {
    return []
  }

  function positionForBottomRight(): [number, number] {
    return i18n.dir() === 'ltr'
      ? [window.innerWidth - 270, window.innerHeight - 420]
      : [-10, window.innerHeight - 420]
  }

  // Video-based onboarding steps
  const onboardMainMessageVideo: StepType = {
    selector: '#app-root',
    styles: {
      maskArea: (base) => ({
        ...base,
        rx: 0,
        padding: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none'
      }),
      maskWrapper: (base) => ({
        ...base,
        color: 'rgba(0, 0, 0, 0.0)'
      })
    },
    position: positionForBottomRight,
    content: (
      <VideoOnboardingContent
        id="onboarding-initial"
        veryFirst
        title={t('onboardGroupClickTitle')}
        videoUrl={videoByIdAndLanguage('onboarding-initial')}
        onVideoEnded={() => {
          completeTutorialStep('#app-root')
        }}
      />
    ), // Empty - handled by Content component
    actionAfter: () => {}
  }

  const onboardGroupSectionMessageVideo: StepType = {
    selector: '#first-group',
    position: 'right',
    content: (
      <VideoOnboardingContent
        id="onboarding-group-section"
        title={t('onboardGroupClickTitle')}
        videoUrl={videoByIdAndLanguage('onboarding-group-section')}
        onVideoEnded={() => {
          completeTutorialStep('#first-group')
        }}
      />
    ),
    actionAfter: () => {}
  }

  const onboardSharedCalendarScreenVideo: StepType = {
    selector: '#calendar-root',
    position: i18n.dir() === 'ltr' ? 'left' : 'right',
    content: (
      <VideoOnboardingContent
        id="onboarding-calendar-view"
        title={t('onboardGroupDetailsTitle')}
        videoUrl={videoByIdAndLanguage('onboarding-calendar-view')}
        onVideoEnded={() => {
          completeTutorialStep('#calendar-root')
        }}
        conditionalStep={(props) => {
          setTimeout(() => {
            if (
              !isTrulyEmpty(LocalStorage.get(LocalStorageKey.SELECTED_GROUP)) &&
              isTrulyEmpty(LocalStorage.get(LocalStorageKey.SHOWING_GROUP_INFO))
            ) {
              props.setIsOpen(true)
            } else {
              props.setIsOpen(false)
            }
          }, 250)
        }}
      />
    ),
    actionAfter: () => {}
  }

  const onboardSharedCalendarInfoVideo: StepType = {
    selector: '#calendar-info-calendar',
    content: (
      <VideoOnboardingContent
        id="onboarding-shared-calendar-details"
        title={t('onboardTapGroupInfoTitle')}
        videoUrl={videoByIdAndLanguage('onboarding-shared-calendar-details')}
        onVideoEnded={() => {
          completeTutorialStep('#calendar-info-calendar')
        }}
        nextButtonAction={() => {
          clickElementById('calendar-info-calendar')
        }}
      />
    ),
    actionAfter: () => {}
  }

  const onboardSharedCalendarInfoDetailsVideo: StepType = {
    selector: '#group-details-popup',
    content: <GroupDetailsContent />,
    actionAfter: () => {}
  }

  const onboardAddSharedCalendarVideo: StepType = {
    selector: '#add-group-button',
    content: (
      <VideoOnboardingContent
        id="onboarding-add-calendar-button"
        title={''}
        videoUrl={videoByIdAndLanguage('onboarding-add-calendar-button')}
        onVideoEnded={() => {
          completeTutorialStep('#add-group-button')
        }}
        nextButtonAction={(tour) => {
          clickElementById('add-group-button')
          if (tour.currentStep === tour.steps.length - 1) {
            tour.setIsOpen(false)
          }
        }}
      />
    ),
    actionAfter: () => {}
  }

  const dimmedEventsMessageVideo: StepType = {
    selector: '#somewhere_dimmed',
    styles: {
      maskArea: (base) => ({
        ...base,
        rx: 0,
        padding: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none'
      }),
      maskWrapper: (base) => ({
        ...base,
        color: 'rgba(0, 0, 0, 0.0)'
      })
    },
    position: positionForBottomRight,
    content: (
      <VideoOnboardingContent
        id="onboarding-dimmed-events"
        title={t('onboardDimmedEventsTitle')}
        videoUrl={videoByIdAndLanguage('onboarding-dimmed-events')}
      />
    ),
    actionAfter: () => {
      completeTutorialStep('#somewhere_dimmed')
    }
  }

  const duplicatedEventsMessageVideo: StepType = {
    selector: '#event-popup-content-box',

    content: (
      <VideoOnboardingContent
        id="onboarding-duplicate"
        title={t('duplicate')}
        videoUrl={videoByIdAndLanguage('onboarding-duplicate')}
      />
    ),
    actionAfter: () => {
      completeTutorialStep('#event-popup-content-box')
    }
  }

  let steps: StepType[] = []

  let ONBOARDING_STEPS_A = [
    onboardMainMessageVideo,
    onboardGroupSectionMessageVideo,
    onboardSharedCalendarScreenVideo,
    onboardSharedCalendarInfoVideo,
    onboardSharedCalendarInfoDetailsVideo,
    onboardAddSharedCalendarVideo
  ]

  if (LocalStorage.get(LocalStorageKey.SELECTED_GROUP) === null) {
    ONBOARDING_STEPS_A = [
      onboardMainMessageVideo,
      onboardGroupSectionMessageVideo,
      onboardSharedCalendarScreenVideo,
      onboardAddSharedCalendarVideo
    ]
  }

  const ONBOARDING_STEPS_B = [onboardMainMessageVideo]

  switch (mode) {
    case ONBOARDING_MODE.FIRST_LOGIN:
      steps = getUserABGroup() === 'A' ? ONBOARDING_STEPS_A : ONBOARDING_STEPS_B
      break
    case ONBOARDING_MODE.DIMMED:
      steps = [dimmedEventsMessageVideo]
      break
    case ONBOARDING_MODE.DUPLICATED:
      steps = [duplicatedEventsMessageVideo]
      break
    case ONBOARDING_MODE.FROM_GROUP:
      steps = [
        onboardGroupSectionMessageVideo,
        onboardSharedCalendarInfoVideo,
        onboardAddSharedCalendarVideo
      ]
      break
    case ONBOARDING_MODE.FROM_ALL:
      steps = [onboardSharedCalendarScreenVideo, onboardAddSharedCalendarVideo]
      break
    default:
      steps = []
  }

  if (
    is24meApp() ||
    LocalStorage.get(LocalStorageKey.USER_FINSHED_ONBOARDING) === '1'
  )
    return []

  const filtered = steps.filter((step) => {
    const state = JSON.parse(
      LocalStorage.get(LocalStorageKey.STATE_DICT) || '{}'
    )

    return !(step.selector.toString() in state)
  })

  Logger.blue('TFMTourProvider', { filtered })

  return filtered
}

function GroupDetailsContent() {
  const calendar = useCalendar()
  return (
    <VideoOnboardingContent
      id="onboarding-shared-calendar-details-popup"
      title={t('onboardGroupDetailsPopupTitle')}
      videoUrl={videoByIdAndLanguage(
        'onboarding-shared-calendar-details-popup'
      )}
      onVideoEnded={() => {
        completeTutorialStep('#group-details-popup')
      }}
      nextButtonAction={() => {
        calendar.onGroupInfo(undefined)
      }}
    />
  )
}

function checkCompletedStep(step: string): boolean {
  const state = JSON.parse(LocalStorage.get(LocalStorageKey.STATE_DICT) || '{}')

  return state[step] === true
}

export function completeTutorialStep(step: string) {
  const existing = JSON.parse(
    LocalStorage.get(LocalStorageKey.STATE_DICT) || '{}'
  )

  LocalStorage.set(
    LocalStorageKey.STATE_DICT,
    JSON.stringify({ ...existing, [step]: true })
  )
}

function clickElementById(elementId: string): void {
  const element = document.getElementById(elementId)
  if (element) {
    // Click the parent element
    element.click()
  } else {
    console.warn(`Element with ID ${elementId} not found.`)
  }
}

function clickElementsByClassName(className: string): void {
  const elements = document.getElementsByClassName(className)
  if (elements.length > 0 && elements[0] instanceof HTMLElement) {
    elements[0].click()
  } else {
    console.warn(
      `No elements with class name ${className} found or the first element is not an HTMLElement.`
    )
  }
}

export function useTfmTourProvider(): TFMTourProviderContextProps {
  return useContext(TFMTourProviderContext)
}

function videoByIdAndLanguage(videoId: string): string {
  let lang = i18n.language
  const supportedLanguages = Object.keys(i18n.options.resources || {})

  if (lang.length > 2) {
    lang = lang.split('-')[0]
  }

  // Map of video IDs to their filenames
  const videoMap: Record<string, string> = {
    'onboarding-initial': 'tip_welcome.mp4',
    'onboarding-group-section': 'tip_calendars_section.mp4',
    'onboarding-calendar-view': 'tip_calendar_view.mp4',
    'onboarding-shared-calendar-details': 'tip_calendar_details.mp4',
    'onboarding-shared-calendar-details-popup':
      'tip_calendar_details_popup.mp4',
    'onboarding-add-calendar-button': 'tip_add_calendar.mp4',
    'onboarding-dimmed-events': 'tip_dimmed_events.mp4',
    'onboarding-duplicate': 'tip_duplicate_events.mp4'
  }

  const filename = videoMap[videoId]

  if (!filename) {
    console.warn(`Video ID "${videoId}" not found`)
    return `https://s3.us-east-1.amazonaws.com/res.twentyfour.me/GroupCal/onboarding_videos/en/tip_welcome.mp4`
  }

  // Determine the language folder - use current language or fallback to 'en'
  const langFolder = supportedLanguages.includes(lang) ? lang : 'en'

  // Standard AWS URL with language subfolder
  return `https://s3.us-east-1.amazonaws.com/res.twentyfour.me/GroupCal/onboarding_videos/${langFolder}/${filename}`
}

export default compose(
  withObservables([], () => ({
    account: ObserveService.Account()
  }))
)(TFMTourProvider as ComponentType<unknown>) as any
